/**
 * Error Classification System
 *
 * This module provides intelligent error analysis and routing decisions for the
 * unified request coordination architecture. It determines how errors should be
 * handled based on their type, status, and configuration.
 */

import {
  type RequestConfig,
  type ErrorClassification,
  type RetryConfig,
  type IErrorClassifier,
  DEFAULT_RETRY_CONFIG,
  ErrorType,
  ErrorCategory,
  ErrorPriority,
} from '../../../types/api';
import {
  isAxiosError,
  isHttpError,
  isNetworkError,
} from '../../../types/api/request.types';
import {
  isExcludedEndpoint,
  isRetryableMethod,
  isRetryableStatusCode,
} from '../../../types/api/retry.types';
import { dayjs } from '../../../utils/date';

// =============================================================================
// ERROR CLASSIFIER IMPLEMENTATION
// =============================================================================

export class ErrorClassifier implements IErrorClassifier {
  private retryConfig: RetryConfig;

  constructor(retryConfig: RetryConfig = DEFAULT_RETRY_CONFIG) {
    this.retryConfig = retryConfig;
  }

  /**
   * Classifies an error and determines how it should be handled
   */
  classifyError(error: Error, config: RequestConfig): ErrorClassification {
    // Initial classification
    const type = this.determineErrorType(error);
    const category = this.determineErrorCategory(error, config);
    const priority = this.determineErrorPriority(error, type);

    // Routing decisions
    const shouldRetry = this.shouldRetryError(error, config, type, category);
    const shouldRefreshAuth = this.shouldRefreshAuthError(error, type);
    const shouldTransform = this.shouldTransformError(error, type, category);

    // Additional properties
    const retryDelay = shouldRetry
      ? this.calculateRetryDelay(config.metadata?.retryCount || 0, {
          type,
          category,
          shouldRetry,
          shouldRefreshAuth,
          shouldTransform,
          priority,
        } as ErrorClassification)
      : undefined;
    const circuitBreakerTrip = this.isCircuitBreakerCandidate({
      type,
      category,
    } as ErrorClassification);

    return {
      type,
      category,
      shouldRetry,
      shouldRefreshAuth,
      shouldTransform,
      retryDelay,
      circuitBreakerTrip,
      priority,
    };
  }

  /**
   * Determines if an error classification indicates retry should be attempted
   */
  shouldRetry(classification: ErrorClassification): boolean {
    return classification.shouldRetry;
  }

  /**
   * Determines if an error classification indicates auth refresh should be attempted
   */
  shouldRefreshAuth(classification: ErrorClassification): boolean {
    return classification.shouldRefreshAuth;
  }

  /**
   * Determines if an error classification indicates error should be transformed
   */
  shouldTransform(classification: ErrorClassification): boolean {
    return classification.shouldTransform;
  }

  /**
   * Calculates retry delay based on attempt number and classification
   */
  calculateRetryDelay(
    attempt: number,
    classification: ErrorClassification
  ): number {
    if (!classification.shouldRetry) {
      return 0;
    }

    // Handle rate limiting with specific delay
    if (classification.type === ErrorType.RATE_LIMIT) {
      return this.calculateRateLimitDelay();
    }

    // Exponential backoff with jitter
    const baseDelay = this.retryConfig.baseDelay;
    const exponentialDelay =
      baseDelay * Math.pow(this.retryConfig.exponentialBase, attempt);
    const maxDelay = this.retryConfig.maxDelay;

    // Apply jitter to prevent thundering herd
    const jitter = this.retryConfig.jitterFactor * Math.random();
    const jitterMultiplier = 1 + (jitter - this.retryConfig.jitterFactor / 2);

    const delay = Math.min(exponentialDelay * jitterMultiplier, maxDelay);

    return Math.round(delay);
  }

  /**
   * Determines if an error should trigger circuit breaker logic
   */
  isCircuitBreakerCandidate(classification: ErrorClassification): boolean {
    return (
      classification.category === ErrorCategory.RETRYABLE ||
      classification.type === ErrorType.SERVER_ERROR ||
      classification.type === ErrorType.TIMEOUT ||
      classification.type === ErrorType.NETWORK
    );
  }

  /**
   * Updates retry configuration
   */
  updateRetryConfig(config: Partial<RetryConfig>): void {
    this.retryConfig = { ...this.retryConfig, ...config };
  }

  // =============================================================================
  // PRIVATE CLASSIFICATION METHODS
  // =============================================================================

  private determineErrorType(error: Error): ErrorType {
    if (!isAxiosError(error)) {
      return ErrorType.UNKNOWN;
    }

    // Network errors (no response)
    if (isNetworkError(error)) {
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        return ErrorType.TIMEOUT;
      }
      if (error.code === 'ERR_CANCELED') {
        return ErrorType.CANCELLED;
      }
      return ErrorType.NETWORK;
    }

    // HTTP errors (has response)
    if (isHttpError(error)) {
      const status = error.response!.status;

      if (status === 401) {
        return ErrorType.AUTHENTICATION;
      }
      if (status === 403) {
        return ErrorType.AUTHORIZATION;
      }
      if (status === 422 || status === 400) {
        return ErrorType.VALIDATION;
      }
      if (status === 429) {
        return ErrorType.RATE_LIMIT;
      }
      if (status >= 500) {
        return ErrorType.SERVER_ERROR;
      }
      if (status >= 400) {
        return ErrorType.CLIENT_ERROR;
      }
    }

    return ErrorType.UNKNOWN;
  }

  private determineErrorCategory(
    error: Error,
    config: RequestConfig
  ): ErrorCategory {
    const type = this.determineErrorType(error);

    // Authentication errors require special handling
    if (type === ErrorType.AUTHENTICATION) {
      return ErrorCategory.AUTH_REQUIRED;
    }

    // Client errors generally don't benefit from retries
    if (type === ErrorType.CLIENT_ERROR || type === ErrorType.VALIDATION) {
      return ErrorCategory.NON_RETRYABLE;
    }

    // Authorization errors require user action
    if (type === ErrorType.AUTHORIZATION) {
      return ErrorCategory.USER_ACTION_REQUIRED;
    }

    // Cancelled requests shouldn't be retried
    if (type === ErrorType.CANCELLED) {
      return ErrorCategory.NON_RETRYABLE;
    }

    // Check if endpoint is excluded from retries
    if (config.url && isExcludedEndpoint(config.url, this.retryConfig)) {
      return ErrorCategory.NON_RETRYABLE;
    }

    // Check if method is not retryable
    if (!isRetryableMethod(config.method, this.retryConfig)) {
      return ErrorCategory.NON_RETRYABLE;
    }

    // Check if we've exceeded max retry attempts
    const retryCount = config.metadata?.retryCount || 0;
    if (retryCount >= this.retryConfig.maxAttempts) {
      return ErrorCategory.NON_RETRYABLE;
    }

    // Network, timeout, and server errors are typically retryable
    if (
      type === ErrorType.NETWORK ||
      type === ErrorType.TIMEOUT ||
      type === ErrorType.SERVER_ERROR ||
      type === ErrorType.RATE_LIMIT
    ) {
      return ErrorCategory.RETRYABLE;
    }

    // HTTP errors need status code check
    if (isAxiosError(error) && error.response) {
      const status = error.response.status;
      if (isRetryableStatusCode(status, this.retryConfig)) {
        return ErrorCategory.RETRYABLE;
      }
    }

    return ErrorCategory.NON_RETRYABLE;
  }

  private determineErrorPriority(
    _error: Error,
    type: ErrorType
  ): ErrorPriority {
    switch (type) {
      case ErrorType.NETWORK:
      case ErrorType.SERVER_ERROR:
        return ErrorPriority.HIGH;

      case ErrorType.TIMEOUT:
      case ErrorType.RATE_LIMIT:
        return ErrorPriority.MEDIUM;

      case ErrorType.AUTHENTICATION:
      case ErrorType.AUTHORIZATION:
        return ErrorPriority.CRITICAL;

      case ErrorType.VALIDATION:
      case ErrorType.CLIENT_ERROR:
        return ErrorPriority.LOW;

      case ErrorType.CANCELLED:
        return ErrorPriority.LOW;

      default:
        return ErrorPriority.MEDIUM;
    }
  }

  private shouldRetryError(
    error: Error,
    config: RequestConfig,
    _type: ErrorType,
    category: ErrorCategory
  ): boolean {
    // Don't retry if category explicitly says no
    if (
      category === ErrorCategory.NON_RETRYABLE ||
      category === ErrorCategory.USER_ACTION_REQUIRED
    ) {
      return false;
    }

    // Don't retry auth errors (handled separately)
    if (category === ErrorCategory.AUTH_REQUIRED) {
      return false;
    }

    // Check retry count
    const retryCount = config.metadata?.retryCount || 0;
    if (retryCount >= this.retryConfig.maxAttempts) {
      return false;
    }

    // For retryable category, do additional checks
    if (category === ErrorCategory.RETRYABLE) {
      // Check method
      if (!isRetryableMethod(config.method, this.retryConfig)) {
        return false;
      }

      // Check endpoint exclusions
      if (config.url && isExcludedEndpoint(config.url, this.retryConfig)) {
        return false;
      }

      // For HTTP errors, check status code
      if (isAxiosError(error) && error.response) {
        return isRetryableStatusCode(error.response.status, this.retryConfig);
      }

      return true;
    }

    return false;
  }

  private shouldRefreshAuthError(_error: Error, type: ErrorType): boolean {
    return type === ErrorType.AUTHENTICATION;
  }

  private shouldTransformError(
    _error: Error,
    type: ErrorType,
    category: ErrorCategory
  ): boolean {
    // Always transform errors that won't be retried
    if (
      category === ErrorCategory.NON_RETRYABLE ||
      category === ErrorCategory.USER_ACTION_REQUIRED
    ) {
      return true;
    }

    // Transform client errors for better user experience
    if (type === ErrorType.CLIENT_ERROR || type === ErrorType.VALIDATION) {
      return true;
    }

    // Don't transform retryable errors (they might succeed on retry)
    if (category === ErrorCategory.RETRYABLE) {
      return false;
    }

    return true;
  }

  private calculateRateLimitDelay(): number {
    // Try to extract Retry-After header if this was an HTTP error
    // For now, use a default rate limit delay
    return Math.min(this.retryConfig.baseDelay * 2, this.retryConfig.maxDelay);
  }
}

// =============================================================================
// FACTORY FUNCTION
// =============================================================================

/**
 * Creates a new error classifier instance
 */
export function createErrorClassifier(
  retryConfig?: RetryConfig
): IErrorClassifier {
  return new ErrorClassifier(retryConfig);
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Quick classification for simple cases
 */
export function quickClassifyError(error: Error): {
  isRetryable: boolean;
  isAuthError: boolean;
  isNetworkError: boolean;
  statusCode?: number;
} {
  const classifier = createErrorClassifier();
  const classification = classifier.classifyError(error, {});

  return {
    isRetryable: classification.shouldRetry,
    isAuthError: classification.shouldRefreshAuth,
    isNetworkError: classification.type === ErrorType.NETWORK,
    statusCode: isAxiosError(error) ? error.response?.status : undefined,
  };
}

/**
 * Extracts Retry-After header from HTTP error response
 */
export function extractRetryAfter(error: Error): number | undefined {
  if (!isAxiosError(error) || !error.response) {
    return undefined;
  }

  const retryAfter =
    error.response.headers['retry-after'] ||
    error.response.headers['Retry-After'];

  if (!retryAfter) {
    return undefined;
  }

  // Parse as seconds
  const seconds = parseInt(retryAfter, 10);
  if (!isNaN(seconds)) {
    return seconds * 1000; // Convert to milliseconds
  }

  // Parse as HTTP date
  const date = dayjs(retryAfter);
  if (date.isValid()) {
    return date.diff();
  }

  return undefined;
}
